import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { Radio, Select, Space, Row, Col, Alert, Spin, Button, Typography, Checkbox } from 'antd';
import { InfoCircleOutlined, LoadingOutlined, HeartFilled } from '@ant-design/icons';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { StyledSelect as SharedStyledSelect } from '../../shared/StyledSelect';
import {
  colors,
  StepTitle,
  InfoBox,
  FormSection,
  FormItem,
  SectionTitle,
  ErrorText,
  LargeInput,
  LargeRadio,
  inlineStyles
} from '../styled';
import { AI_VOICE_CALL_CONSENT_TEXT } from '../constants';

const { Paragraph } = Typography;
const { Option } = Select;

// Sentinel ObjectId for walk-in screenings (no scheduled event)
export const WALK_IN_EVENT_ID = '000000000000000000000000';

/**
 * Step 1: Patient Info
 * 
 * This step has dependencies on parent component state for:
 * - isReturningUser / setIsReturningUser: tracks returning vs new patient flow
 * - patientCheckStatus / setPatientCheckStatus: result of patient existence check
 * - existingPatientInfo: matched patient details
 * - checkingPatient: loading state for patient check
 * - availableEvents / loadingEvents: event dropdown data
 * - debouncedCheckPatientExists: debounced patient lookup
 * - t: translation function
 */
const StepPatientInfo = ({
  // Formik props
  values,
  errors,
  touched,
  setFieldValue,
  // Parent state
  isReturningUser,
  setIsReturningUser,
  patientCheckStatus,
  setPatientCheckStatus,
  existingPatientInfo,
  checkingPatient,
  availableEvents,
  loadingEvents,
  debouncedCheckPatientExists,
  onEventChange,
  isFieldVisible = () => true,
  t
}) => (
  <>
    <StepTitle level={4}>
      {isReturningUser ? 'Welcome Back! Please Confirm Your Information' : 'Your Information'}
    </StepTitle>

    {/* Only show InfoBox for returning users, not for new patients */}
    {(isReturningUser || patientCheckStatus === 'found') && (
      <InfoBox>
        <div className="icon"><InfoCircleOutlined /></div>
        <div className="content">
          <Paragraph style={inlineStyles.infoParagraph}>
            Thank you for returning! Please confirm your information and provide consent so our admins can match you with your existing records.
          </Paragraph>
        </div>
      </InfoBox>
    )}

    {/* Event selection — first because it controls form config */}
    <FormSection>
      <FormItem>
        <SectionTitle>{t('step1.event.label')}</SectionTitle>
        <Paragraph style={inlineStyles.detailParagraph}>
          {t('step1.event.description')}
        </Paragraph>
        <SharedStyledSelect
          style={inlineStyles.selectStyle(touched.selectedEvent, errors.selectedEvent)}
          placeholder={loadingEvents ? "Loading bookings..." : "Which event are you at?"}
          value={values.selectedEvent}
          onChange={(value) => {
            setFieldValue('selectedEvent', value);
            if (onEventChange && value !== WALK_IN_EVENT_ID) onEventChange(value);
          }}
          loading={loadingEvents}
          disabled={loadingEvents}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {availableEvents.length === 0 && !loadingEvents && (
            <Option key={WALK_IN_EVENT_ID} value={WALK_IN_EVENT_ID}>
              <span>Walk-in Screening</span>
            </Option>
          )}
          {availableEvents.map(event => {
            const businessEventName = event.businessEventName || event.eventTypeName || 'Event';
            const startDateStr = moment.utc(event.bookingDate).format('MM/DD/YYYY');
            const isMultiDay = event.isMultiDay && event.bookingEndDate;
            const dateLabel = isMultiDay
              ? `${startDateStr} - ${moment.utc(event.bookingEndDate).format('MM/DD/YYYY')}`
              : startDateStr;
            return (
              <Option key={event._id} value={event._id}>
                <span>
                  {businessEventName} - {dateLabel}
                  {isMultiDay && (
                    <span style={{
                      color: '#1890ff',
                      fontWeight: 500,
                      marginLeft: '6px',
                      fontSize: '11px'
                    }}>
                      ({moment.utc(event.bookingEndDate).diff(moment.utc(event.bookingDate), 'days') + 1} days)
                    </span>
                  )}
                  {event.isPreRegistration && (
                    <span style={{
                      color: '#722ed1',
                      fontWeight: 500,
                      marginLeft: '8px',
                      fontSize: '12px'
                    }}>
                      (Pre-Registration)
                    </span>
                  )}
                </span>
              </Option>
            );
          })}
        </SharedStyledSelect>
        <ErrorMessage name="selectedEvent" component={ErrorText} />
      </FormItem>
    </FormSection>

    <FormSection>
      <FormItem>
        <SectionTitle>{t('step1.usedBefore.label')}</SectionTitle>
        <div style={inlineStyles.topMarginDiv}>
          <Radio.Group
            value={values.usedBefore}
            onChange={(e) => {
              setFieldValue('usedBefore', e.target.value);
              if (e.target.value === 'yes') {
                setIsReturningUser(true);
              } else {
                setIsReturningUser(false);
              }
            }}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="yes">{t('step1.usedBefore.yes')}</LargeRadio>
              <LargeRadio value="no">{t('step1.usedBefore.no')}</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="usedBefore" component={ErrorText} />
      </FormItem>
    </FormSection>

    <FormSection>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <FormItem>
            <SectionTitle>{t('step1.firstName.label')}</SectionTitle>
            <Field name="firstName">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder={t('step1.firstName.placeholder')}
                  style={inlineStyles.errorBorderInput(touched.firstName, errors.firstName)}
                />
              )}
            </Field>
            <ErrorMessage name="firstName" component={ErrorText} />
          </FormItem>
        </Col>
        <Col xs={24} sm={12}>
          <FormItem>
            <SectionTitle>{t('step1.lastName.label')}</SectionTitle>
            <Field name="lastName">
              {({ field }) => (
                <LargeInput
                  {...field}
                  placeholder={t('step1.lastName.placeholder')}
                  style={inlineStyles.errorBorderInput(touched.lastName, errors.lastName)}
                />
              )}
            </Field>
            <ErrorMessage name="lastName" component={ErrorText} />
          </FormItem>
        </Col>
      </Row>
      {isFieldVisible('dateOfBirth') && (
      <FormItem>
        <SectionTitle>{t('step1.dateOfBirth.label')}</SectionTitle>
        <DatePicker
          selected={values.dateOfBirth ? moment(values.dateOfBirth, "MM/DD/YYYY").toDate() : null}
          onChange={(date) => {
            const formattedDate = date ? moment(date).format("MM/DD/YYYY") : '';
            setFieldValue('dateOfBirth', formattedDate);
            if (formattedDate && values.emailAddress) {
              debouncedCheckPatientExists(values.emailAddress, formattedDate);
            }
          }}
          dateFormat="MM/dd/yyyy"
          placeholderText={t('step1.dateOfBirth.placeholder')}
          maxDate={new Date()}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          yearDropdownItemNumber={100}
          scrollableYearDropdown
          openToDate={new Date(1990, 0, 1)}
          customInput={
            <LargeInput 
              style={inlineStyles.errorBorderInput(touched.dateOfBirth, errors.dateOfBirth)}
            />
          }
        />
        <ErrorMessage name="dateOfBirth" component={ErrorText} />
      </FormItem>
      )}

      <FormItem>
        <SectionTitle>{t('step1.email.label')}</SectionTitle>
        <Field name="emailAddress">
          {({ field }) => (
            <LargeInput
              {...field}
              placeholder={t('step1.email.placeholder')}
              type="email"
              style={inlineStyles.errorBorderInput(touched.emailAddress, errors.emailAddress)}
              onChange={(e) => {
                setFieldValue('emailAddress', e.target.value);
                if (e.target.value && values.dateOfBirth) {
                  debouncedCheckPatientExists(e.target.value, values.dateOfBirth);
                }
              }}
            />
          )}
        </Field>
        <ErrorMessage name="emailAddress" component={ErrorText} />
      </FormItem>

      {checkingPatient && (
        <div style={inlineStyles.centerLoadingDiv}>
          <Spin indicator={<LoadingOutlined style={inlineStyles.loadingIcon} spin />} />
          <span style={inlineStyles.loadingSpan}>Checking patient records...</span>
        </div>
      )}

      {patientCheckStatus === 'found' && existingPatientInfo && (
        <Alert
          message="Returning Patient Found"
          description={existingPatientInfo.message || "We found your existing records in our system. You'll be able to complete a simplified form."}
          type="success"
          showIcon
          style={inlineStyles.alertStyle}
        />
      )}

      {patientCheckStatus === 'duplicates' && existingPatientInfo && values.usedBefore === 'no' && (
        <Alert
          message="Potential Duplicate Found"
          description={
            <div>
              <Paragraph style={{ marginBottom: '16px' }}>
                {existingPatientInfo.message || "We found a similar email address in our system."}
              </Paragraph>
              <Paragraph style={{ marginBottom: '12px', fontWeight: 500 }}>
                Have you had a heart health screening before from BHA?
              </Paragraph>
              <Space>
                <Button 
                  type="primary"
                  onClick={() => {
                    setFieldValue('usedBefore', 'yes');
                    setIsReturningUser(true);
                    setPatientCheckStatus('found');
                  }}
                  style={{ 
                    backgroundColor: colors.primary,
                    borderColor: colors.primary
                  }}
                >
                  Yes
                </Button>
                <Button
                  onClick={() => {
                    setFieldValue('usedBefore', 'no');
                    setIsReturningUser(false);
                    setPatientCheckStatus('not_found');
                  }}
                >
                  No
                </Button>
              </Space>
            </div>
          }
          type="warning"
          showIcon
          style={inlineStyles.alertStyle}
        />
      )}

      {patientCheckStatus === 'not_found' && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            animation: 'fadeInSlideDown 0.5s ease-out',
            opacity: 1
          }}
        >
          <HeartFilled 
            style={{ 
              fontSize: '22px', 
              color: '#1a1a1a',
              animation: 'heartbeat 1.5s infinite',
              display: 'inline-block',
              flexShrink: 0,
              marginTop: '2px'
            }} 
          />
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              color: 'rgba(0, 0, 0, 0.85)',
              marginBottom: '4px'
            }}>
              New Member
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: 'rgba(0, 0, 0, 0.65)',
              lineHeight: '1.5'
            }}>
              Welcome! Please complete the full form to register for heart health screening.
            </div>
          </div>
        </div>
      )}
    </FormSection>

    {/* Referral questions: returning users skip the steps where new members answer these */}
    {isReturningUser && (isFieldVisible('insuranceCoverage') || isFieldVisible('mentalHealthInterest') || isFieldVisible('doctorHelpInterest')) && (
      <FormSection>
        {isFieldVisible('insuranceCoverage') && (
        <FormItem>
          <SectionTitle>Current Insurance Coverage</SectionTitle>
          <SharedStyledSelect
            style={inlineStyles.selectStyle(touched.insuranceCoverage, errors.insuranceCoverage)}
            placeholder="Choose Your Insurance Coverage"
            value={values.insuranceCoverage}
            onChange={(value) => setFieldValue('insuranceCoverage', value)}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            <Option value="private">Private</Option>
            <Option value="insuredThroughEmployer">Insured through employer</Option>
            <Option value="medicaid">Medicaid</Option>
            <Option value="medicare">Medicare</Option>
            <Option value="military">Military</Option>
            <Option value="noCoverage">No coverage</Option>
          </SharedStyledSelect>
          <ErrorMessage name="insuranceCoverage" component={ErrorText} />
        </FormItem>
        )}

        {isFieldVisible('doctorHelpInterest') && (
        <FormItem>
          <SectionTitle>Would you like help finding a doctor?</SectionTitle>
          <div style={inlineStyles.topMarginDiv}>
            <Radio.Group
              value={values.doctorHelpInterest}
              onChange={(e) => setFieldValue('doctorHelpInterest', e.target.value)}
            >
              <Space direction="horizontal" size="large" wrap>
                <LargeRadio value="yes">Yes</LargeRadio>
                <LargeRadio value="no">No</LargeRadio>
              </Space>
            </Radio.Group>
          </div>
          <ErrorMessage name="doctorHelpInterest" component={ErrorText} />
        </FormItem>
        )}

        {isFieldVisible('mentalHealthInterest') && (
        <FormItem>
          <SectionTitle>Would you be interested in our free mental health services?</SectionTitle>
          <div style={inlineStyles.topMarginDiv}>
            <Radio.Group
              value={values.mentalHealthInterest}
              onChange={(e) => setFieldValue('mentalHealthInterest', e.target.value)}
            >
              <Space direction="horizontal" size="large" wrap>
                <LargeRadio value="yes">Yes</LargeRadio>
                <LargeRadio value="no">No</LargeRadio>
              </Space>
            </Radio.Group>
          </div>
          <ErrorMessage name="mentalHealthInterest" component={ErrorText} />
        </FormItem>
        )}
      </FormSection>
    )}

    {/* Show additional fields for returning users */}
    {isReturningUser && isFieldVisible('mediaRelease') && (
      <>
        <FormSection>
          <FormItem>
            <SectionTitle>Media Release</SectionTitle>
            <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '4px', marginBottom: '12px' }}>
              I acknowledge that, while participating in this screening, I may be photographed, and or videotaped.
              Black Heart Association, and partners, may use this material for promotional, educational and advertising purposes.
            </Paragraph>
            <div>
              <Radio.Group
                value={values.mediaRelease}
                onChange={(e) => setFieldValue('mediaRelease', e.target.value)}
              >
                <Space direction="vertical">
                  <LargeRadio value="yes">I acknowledge</LargeRadio>
                  <LargeRadio value="no">I do not consent</LargeRadio>
                </Space>
              </Radio.Group>
            </div>
            <ErrorMessage name="mediaRelease" component={ErrorText} />
          </FormItem>
        </FormSection>

        {/* SMS Consent for returning users */}
        {isFieldVisible('transactionalSmsConsent') && (
          <FormSection>
            <FormItem>
              <SectionTitle>SMS Consent</SectionTitle>
              <div>
                <Checkbox
                  checked={values.transactionalSmsConsent}
                  onChange={(e) => setFieldValue('transactionalSmsConsent', e.target.checked)}
                >
                  I agree to receive transactional SMS messages (e.g., appointment confirmations, screening results) from Black Heart Association. Message and data rates may apply.
                </Checkbox>
              </div>
            </FormItem>
          </FormSection>
        )}

        {isFieldVisible('aiVoiceCallConsent') && (
          <FormSection>
            <FormItem>
              <SectionTitle>Phone Call Consent</SectionTitle>
              <Checkbox
                checked={!!values.aiVoiceCallConsent}
                onChange={(e) => setFieldValue('aiVoiceCallConsent', e.target.checked)}
              >
                {AI_VOICE_CALL_CONSENT_TEXT}
              </Checkbox>
            </FormItem>
          </FormSection>
        )}
      </>
    )}
  </>
);

export default StepPatientInfo;
